import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import React, { FC, useState } from 'react';

import styles from './SelectableField.module.scss';
import SelectedIcon from '@lmt/unicorn-shared/app/icons/selected.svg?react';
import NotSelectedIcon from '@lmt/unicorn-shared/app/icons/not-selected.svg?react';
import { QueryFactory } from '@lmt/shared-types';
import { DataBlockTableColumnDto, DataType } from '@lmt/shared-types';
import { useEffect } from 'react';
import { FieldType } from './common';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useTranslation } from 'react-i18next';
import {
  getFormattedDate,
  getFormattedDateTime,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { addMilliseconds, startOfToday } from 'date-fns';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { StyledModal } from '@lmt/unicorn-shared/app/components/common/Modal';
import { CustomFieldTable } from './CustomFieldTable';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

export interface SelectableFieldProps {
  title?: string;
  isSelected: boolean;
  onClick?: () => void;
  className?: string;
  value: any;
  fieldType: FieldType;
  dataType?: DataType;
  dictionaryId?: number;
  columns?: DataBlockTableColumnDto[];
  fieldName: string;
}

export const SelectableField: FC<SelectableFieldProps> = (props) => {
  const {
    title,
    isSelected,
    fieldType,
    value,
    dictionaryId,
    dataType,
    onClick,
    columns,
    fieldName,
  } = props;
  const i18next = useScopedTranslation('merge_assistant.merge_pair');
  const i18n = useTranslation();
  const [displayValue, setDisplayValue] = useState<string>();
  const [isOpenTable, setOpenTable] = useState<boolean>(false);

  const dictionaryItemsQuery =
    QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
      { dictionaryId, all: true },
      {
        enabled: !!dictionaryId,
      },
    );

  useEffect(() => {
    if (fieldType === FieldType.property) {
      setDisplayValue(value == null ? undefined : value.toString());
    } else {
      setDisplayValue(getCustomValue());
    }
  }, [!!dictionaryItemsQuery.data, value]);

  function getCustomValue(): string | undefined {
    if (!dataType || value == null || value === '') {
      return undefined;
    }
    if (dataType === DataType.DateOnly) {
      return getFormattedDate(new Date(value.toString()));
    } else if (dataType === DataType.TimeOnly) {
      const parsedIntValue = parseInt(value);
      if (isNaN(parsedIntValue)) {
        return undefined;
      } else {
        return localFormat(
          addMilliseconds(startOfToday(), parsedIntValue),
          'p',
        );
      }
    } else if (dataType === DataType.DateTime) {
      return getFormattedDateTime(new Date(value));
    } else if (dataType === DataType.Table) {
      return undefined;
    } else if (dataType === DataType.Dictionary) {
      const dictionaryValues: string[] = value.toString().split(',');
      return dictionaryValues
        .map((value) => {
          const item = (dictionaryItemsQuery.data?.data || []).find(
            (item) =>
              item.dictionaryId === dictionaryId && item.id === parseInt(value),
          );
          if (item) {
            return i18n.t(item.value as any, { ns: 'data-block-fields' });
          } else {
            return value.toString();
          }
        })
        .join(', ');
    } else {
      return value.toString();
    }
  }
  const isTable = dataType === DataType.Table && value.length !== 0;

  const displayValueComponent = isTable ? (
    <UniLink
      color={ButtonColor.Primary}
      title={i18next.t('table')}
      onClick={(e) => {
        e.stopPropagation();
        setOpenTable(true);
      }}
    />
  ) : (
    (displayValue ?? i18next.t('no_data'))
  );

  return (
    <div className={props.className} data-test-id="merge-field-option">
      {title ? (
        <Field title={title} testId="merge-field-option-title">
          {displayValueComponent}
        </Field>
      ) : (
        <div
          data-selected={isSelected}
          className={styles.container}
          onClick={onClick}
        >
          <div data-empty={!displayValue} className={styles.title}>
            {displayValueComponent}
          </div>
          <div className={styles.badge}>
            {isSelected ? (
              <SelectedIcon className={styles.selectedIcon} />
            ) : (
              <NotSelectedIcon className={styles.notSelectedIcon} />
            )}
          </div>
        </div>
      )}
      <StyledModal
        contentLabel={fieldName}
        isOpen={isOpenTable}
        onRequestClose={() => setOpenTable(false)}
      >
        {isTable && !!columns && (
          <CustomFieldTable value={value} columns={columns} />
        )}
      </StyledModal>
    </div>
  );
};
